// useApi.js - Custom hook for handling API requests with error handling
import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for handling API requests with built-in error handling
 * @param {Function} apiFunction - The API function to call (should return a Promise)
 * @param {Object} options - Additional options
 * @returns {Object} - Request state and control functions
 */
const useApi = (apiFunction, options = {}) => {
  const { 
    autoExecuteOnMount = false, 
    dependencies = [],
    onSuccess,
    onError
  } = options;
  
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Function to execute the API call
  const execute = useCallback(async (...args) => {
    if (!navigator.onLine) {
      const offlineError = new Error('You are currently offline. Please check your internet connection and try again.');
      offlineError.isOfflineError = true;
      setError(offlineError);
      if (onError) onError(offlineError);
      return null;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const result = await apiFunction(...args);
      setData(result);
      if (onSuccess) onSuccess(result);
      return result;
    } catch (err) {
      console.error('API request failed:', err);
      
      // Enhance error with more user-friendly messages
      if (err.code === 'permission-denied') {
        err.message = 'You don\'t have permission to access this resource.';
      } else if (err.code === 'unavailable') {
        err.message = 'The service is currently unavailable. Please try again later.';
      } else if (err.code === 'failed-precondition') {
        err.message = 'Cannot complete this action while offline.';
      } else if (err.code === 'not-found') {
        err.message = 'The requested resource was not found.';
      } else if (!err.message) {
        err.message = 'An error occurred while processing your request.';
      }
      
      setError(err);
      if (onError) onError(err);
      return null;
    } finally {
      // FIXED: Set loading to false immediately without setTimeout
      setIsLoading(false);
    }
  }, [apiFunction, onSuccess, onError]);

  // Auto-execute on mount if option is enabled
  useEffect(() => {
    if (autoExecuteOnMount) {
      execute();
    }
  }, [autoExecuteOnMount, execute, ...dependencies]);

  // Reset error when coming back online
  useEffect(() => {
    const handleOnline = () => {
      if (error?.isOfflineError) {
        setError(null);
      }
    };
    
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [error]);

  // Function to reset the request state
  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    data,
    error,
    isLoading,
    execute,
    reset
  };
};

export default useApi;